import React, { useEffect, useRef, useState } from 'react';

// Declare YouGlish globally for TypeScript
declare global {
  interface Window {
    onYouglishAPIReady: () => void;
    YG: any;
  }
}

interface WordOfTheDayCardProps {
  word: string;
  definition?: string;
  hideHeader?: boolean;
}

export default function WordOfTheDayCard({ word, definition, hideHeader = false }: WordOfTheDayCardProps) {
  const widgetRef = useRef<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(1);
  
  // A unique container ID is needed if multiple instances are rendered
  const containerId = React.useMemo(() => "youglish-widget-" + Math.random().toString(36).substr(2, 9), []);

  if (!word || word.trim() === '') {
    return null;
  }

  useEffect(() => {
    // 1. Define the callback for when the script loads
    window.onYouglishAPIReady = () => {
      // Ensure the widget hasn't already been initialized
      if (widgetRef.current) return;
      
      widgetRef.current = new window.YG.Widget(containerId, {
        components: 8, // Hides the search bar, leaves player and captions
        autoStart: 0, // Prevents automatic playback
        events: {
          onFetchDone: (event: any) => console.log("YouGlish fetch complete", event),
          onVideoChange: (event: any) => {
            console.log("YouGlish video changed", event);
            if (event && typeof event.trackNumber === 'number') {
              setCurrentTrack(event.trackNumber);
            }
          },
          onCaptionConsumed: () => {
            // Pause the video after the current sentence ends
            if (widgetRef.current) {
              widgetRef.current.pause();
            }
          }
        }
      });
      setIsReady(true);
    };

    // 2. Load the script if it hasn't been loaded
    const scriptSrc = "https://youglish.com/public/emb/widget.js";
    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const tag = document.createElement('script');
      tag.src = scriptSrc;
      tag.async = true;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag?.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }
    } else {
      // If script is already loaded but widget isn't initialized
      if (window.YG && !widgetRef.current) {
        // Use timeout to ensure DOM is ready
        setTimeout(() => window.onYouglishAPIReady(), 100);
      }
    }

    return () => {
      // Optional cleanup
      setIsReady(false);
    };
  }, []);

  // 3. Fetch the word when widget is ready or word changes
  useEffect(() => {
    if (isReady && widgetRef.current && word && word.trim() !== '') {
      widgetRef.current.fetch(word.trim(), "english");
    }
  }, [isReady, word]);

  const handleNext = () => {
    if (widgetRef.current) widgetRef.current.next();
  };

  const handlePrevious = () => {
    if (widgetRef.current) widgetRef.current.previous();
  };

  const handleReplay = () => {
    if (widgetRef.current) widgetRef.current.replay();
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 w-full max-w-2xl mx-auto my-8 transition-all hover:shadow-2xl">
      {/* Top Section: Word and Definition */}
      {!hideHeader && (
        <div className="p-8 pb-6 border-b border-gray-50 bg-gradient-to-br from-purple-50 to-white">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Word of the Day
            </span>
          </div>
          <h2 className="text-4xl font-extrabold text-gray-900 mb-4 capitalize">
            {word}
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            {definition}
          </p>
        </div>
      )}
      
      {/* Bottom Section: YouGlish Video Integration */}
      <div className={`p-6 ${hideHeader ? 'bg-white' : 'bg-gray-50'}`}>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
              Hear it in context
            </h3>
          </div>
          
          {/* Custom Controls */}
          {isReady && (
            <div className="flex items-center gap-2">
              {currentTrack > 1 && (
                <button 
                  onClick={handlePrevious}
                  className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  Previous
                </button>
              )}
              <button 
                onClick={handleReplay}
                className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
              >
                Replay
              </button>
              <button 
                onClick={handleNext}
                className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
              >
                Next Example
              </button>
            </div>
          )}
        </div>
        
        {/* YouGlish Container (Cropped to hide YouGlish/Fluently ads) */}
        <div className="w-full rounded-xl overflow-hidden shadow-inner bg-black relative" style={{ height: '340px' }}>
          <div style={{ position: 'absolute', top: '-45px', bottom: '-10px', left: 0, right: 0 }}>
            <div id={containerId} style={{ width: '100%', height: '100%' }}></div>
          </div>
          
          {/* Fallback/Loading state while YouGlish loads */}
          {!isReady && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100 z-10">
              <div className="flex flex-col items-center">
                <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mb-3"></div>
                <p>Loading video player...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
